import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Save, Upload, Star, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    parent_name: "", city: "", child_age: "", pack_id: "", pack_title: "",
    rating: 5, text: "", published: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReviews();
    api.get("/admin/packs").then(({ data }) => setPacks(data)).catch(() => {});
  }, []);

  async function fetchReviews() {
    try {
      const { data } = await api.get("/admin/reviews");
      setReviews(data);
    } catch { toast.error("Failed to load reviews"); }
    finally { setLoading(false); }
  }

  function openNew() {
    setForm({ parent_name: "", city: "", child_age: "", pack_id: "", pack_title: "", rating: 5, text: "", published: false });
    setEditing("new");
  }

  function openEdit(r) {
    setForm({
      parent_name: r.parent_name || "", city: r.city || "", child_age: r.child_age || "",
      pack_id: r.pack_id || "", pack_title: r.pack_title || "",
      rating: r.rating || 5, text: r.text || "", published: r.published || false,
    });
    setEditing(r._id);
  }

  async function handleSave() {
    if (!form.parent_name.trim() || !form.text.trim()) { toast.error("Name and review text are required"); return; }
    setSaving(true);
    try {
      const packTitle = form.pack_id ? (packs.find((p) => p._id === form.pack_id)?.title || "") : "";
      const payload = { ...form, pack_title: packTitle };
      if (editing === "new") {
        await api.post("/admin/reviews", payload);
        toast.success("Review added");
      } else {
        await api.put(`/admin/reviews/${editing}`, payload);
        toast.success("Review updated");
      }
      setEditing(null);
      fetchReviews();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); fetchReviews(); }
    catch { toast.error("Failed to delete"); }
  }

  async function handleUploadPhoto(reviewId) {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      const fd = new FormData(); fd.append("file", f);
      try {
        await api.post(`/admin/reviews/${reviewId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Photo added"); fetchReviews();
      } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    };
    input.click();
  }

  async function handleDeletePhoto(reviewId, photoId) {
    try { await api.delete(`/admin/reviews/${reviewId}/photos/${photoId}`); toast.success("Photo removed"); fetchReviews(); }
    catch { toast.error("Failed"); }
  }

  if (loading) return <div className="py-8 text-center text-slate-400">Loading reviews...</div>;

  return (
    <div className="mt-4 space-y-3">
      <Button onClick={openNew} className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-11 font-bold">
        <Plus className="w-4 h-4 mr-2" /> Add Review
      </Button>

      {reviews.length === 0 ? (
        <p className="text-center py-8 text-slate-400 font-body">No reviews yet</p>
      ) : (
        reviews.map((r) => (
          <div key={r._id} className="bg-white rounded-2xl p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm text-slate-800">{r.parent_name}</p>
                  {r.published ? (
                    <Badge className="text-[10px] bg-green-100 text-green-700 border-0">Published</Badge>
                  ) : (
                    <Badge className="text-[10px] bg-slate-100 text-slate-500 border-0">Draft</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">{r.city}{r.child_age ? ` · Child age ${r.child_age}` : ""}{r.pack_title ? ` · ${r.pack_title}` : ""}</p>
                <div className="flex gap-0.5 mt-1">
                  {[1,2,3,4,5].map((s) => (
                    <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                  ))}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="icon" className="h-7 w-7 rounded-lg" onClick={() => openEdit(r)}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button variant="outline" size="icon" className="h-7 w-7 rounded-lg text-red-500" onClick={() => handleDelete(r._id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-600 font-body line-clamp-2">{r.text}</p>
            <div className="flex gap-2 items-center">
              {(r.photos || []).map((p) => (
                <div key={p.id} className="relative w-12 h-12 rounded-lg overflow-hidden bg-slate-100 group">
                  <img src={`${API_URL}/api/images/${p.storage_path}`} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => handleDeletePhoto(r._id, p.id)}
                    className="absolute inset-0 bg-red-500/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {(r.photos || []).length < 3 && (
                <button onClick={() => handleUploadPhoto(r._id)}
                  className="w-12 h-12 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300 hover:text-slate-500 hover:border-slate-400 transition-colors">
                  <ImageIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))
      )}

      {/* Edit/New Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-2xl max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing === "new" ? "Add Review" : "Edit Review"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
              placeholder="Parent's name" className="rounded-xl" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="City" className="rounded-xl" />
              <Input value={form.child_age} onChange={(e) => setForm({ ...form, child_age: e.target.value })}
                placeholder="Child's age (e.g., 4)" className="rounded-xl" />
            </div>
            <Select value={form.pack_id || "none"} onValueChange={(v) => setForm({ ...form, pack_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kit (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific kit</SelectItem>
                {packs.map((p) => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Rating</label>
              <div className="flex gap-1">
                {[1,2,3,4,5].map((s) => (
                  <button key={s} onClick={() => setForm({ ...form, rating: s })} className="p-0.5">
                    <Star className={`w-6 h-6 transition-colors ${s <= form.rating ? "text-amber-400 fill-amber-400" : "text-slate-200 hover:text-amber-200"}`} />
                  </button>
                ))}
              </div>
            </div>
            <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Review text..." rows={4} className="rounded-xl resize-none" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Published</span>
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-coral hover:bg-coral-hover text-white rounded-full font-bold" disabled={saving} onClick={handleSave}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              {editing === "new" ? "Add" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
